// Куда человек попадает после входа, и чем держится возврат на прерванный адрес.
//
// ⚠⚠ **Правило (`G93`, решение оркестратора 71 (б)):** вход ведёт на `S-52` у
// всех и всегда — в том числе первый вход и вход по закладке без сессии.
// Исключение ровно одно: **повторный вход после сессии, закончившейся посреди
// работы**, и только если прерванная поверхность человеку **по-прежнему
// выдана**; иначе снова `S-52`. Возврат — продолжение прерванной работы, а не
// старт.
//
// ⚠⚠ **Чем держится прерванный адрес: клиентом, а не сервером.** Сервер его не
// хранит (перенос из `carry.md`, строка таска 11). Проверено по адресам:
// `server/fortify/api/auth/routes.py:331`–`:336` — `session_of` кладёт в
// `SessionExpired.return_to` **путь ручки** (`str(request.url.path)`, то есть
// `/api/auth/me`), а не адрес экрана, на котором человек работал; поля под
// экранный адрес нет ни в `AppSession`, ни в ответе `/login`. Значит,
// единственный, кто знает, где человека прервали, — сам браузер.
//
// ⚠ Хранилище — `sessionStorage`, и это часть правила, а не деталь:
//
// - `localStorage` пережил бы закрытие браузера, и человек, вернувшийся назавтра
//   по закладке, попал бы не на дашборд, а туда, где его прервали вчера, — то
//   есть ровно в тот случай, который `G93` называет стартом.
// - память страницы (обычная переменная) не переживает перезагрузку, а
//   закончившаяся сессия часто именно перезагрузкой и обнаруживается.
//
// ⚠ Записывается **код поверхности**, а не только адрес: право проверяется по
// коду (`/me`.surfaces), и адрес, разобранный обратно в код, был бы вторым
// способом отвечать на тот же вопрос.

const KEY = "fortify.interrupted";

/** Где человека прервала закончившаяся сессия. */
export interface Interrupted {
  /** Код поверхности, `S-NN`. По нему проверяется, выдана ли она сейчас. */
  readonly code: string;
  /** Адрес, на который вернуть. */
  readonly path: string;
}

function store(): Storage | null {
  // ⚠ Доступ к хранилищу бросает исключение, а не возвращает null, когда
  // хранилище запрещено настройками браузера. Вход обязан работать и там:
  // без памяти о прерванном адресе человек попадёт на дашборд — это законный
  // исход правила, а не отказ.
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Запомнить, где человека прервали.
 *
 * ⚠ Сам вход не запоминается: `S-00` — корень навигации (`G71`), в него не
 * ведёт ни одна строка, и «вернуть на вход после входа» смысла не имеет.
 */
export function remember(where: Interrupted): void {
  if (where.code === "S-00") return;
  store()?.setItem(KEY, JSON.stringify(where));
}

/** Что запомнено. Ничего не запомнено либо запись испорчена — `null`. */
export function interrupted(): Interrupted | null {
  const raw = store()?.getItem(KEY);
  if (raw === null || raw === undefined) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    const { code, path } = parsed as Partial<Interrupted>;
    if (typeof code !== "string" || typeof path !== "string") return null;
    if (code === "" || !path.startsWith("/")) return null;
    return { code, path };
  } catch {
    return null;
  }
}

/**
 * Записать, где человека прервали, по живому адресу браузера.
 *
 * ⚠⚠ **Это и есть проводка механизма**, которой не было: `remember` звался
 * только из теста, и ветка «вернуть туда, где остановился» в жизни не
 * наступала ни разу. Здесь адрес превращается в код поверхности по реестру —
 * тому же, из которого собраны все адреса, — и записывается.
 *
 * ⚠ Поверхность по адресу не нашлась (каталог компонентов, неизвестный адрес)
 * — не записывается ничего: возвращать некуда, и выдуманный код хуже пустоты.
 * Вход `S-00` отсеивает сам `remember`: он корень навигации (`G71`).
 *
 * ⚠ Сегодня в системе одна поверхность, и она же вход, поэтому записывать
 * пока нечего — это верное поведение, а не пробел. Чего здесь нет и быть не
 * может до оболочки: **перехвата 401 у запросов всех поверхностей**. Сейчас
 * ответ о законченной сессии видит только `S-00`; общий перехват живёт в
 * транспорте оболочки и заводится вместе с ней.
 */
export function rememberInterrupted(
  known: readonly { readonly code: string; readonly path: string }[],
  at: string,
): void {
  const found = known.find((one) => one.path === at);
  if (found === undefined) return;
  remember({ code: found.code, path: found.path });
}

/** Забыть прерванный адрес. Зовётся сразу после того, как им воспользовались. */
export function forget(): void {
  store()?.removeItem(KEY);
}

// --- «Сесія закінчилась» ------------------------------------------------------
//
// ⚠⚠ **Зачем отдельная отметка, если сервер и так отвечает 401.** Ручка `/me`
// отвечает «сесія закінчилась» **и тогда, когда сессии не было вовсе**:
// `session_of` (`routes.py:331`–`:336`) бросает то же исключение на отсутствие
// cookie. Показать этот текст всякому, кто впервые открыл адрес, нельзя — он
// сообщил бы человеку о перерыве, которого не было, и обещал бы возврат туда,
// где тот не работал. Поэтому «сесія закінчилась» показывается только там, где
// **этот же браузер** видел живую сессию: отметку ставит тот, кого прервали
// посреди работы.
//
// ⚠ Хранится текст сервера, а не наш: слова о конце сессии выбирает одно место.

const OVER_KEY = "fortify.session-over";

/** Отметить, что сессия кончилась посреди работы, и запомнить слова сервера. */
export function rememberSessionOver(text: string): void {
  store()?.setItem(OVER_KEY, text);
}

/** Слова о законченной сессии, если этот браузер видел живую сессию. */
export function sessionOver(): string | null {
  return store()?.getItem(OVER_KEY) ?? null;
}

/** Забыть отметку. Зовётся, как только человек вошёл заново. */
export function forgetSessionOver(): void {
  store()?.removeItem(OVER_KEY);
}

/** Почему человек оказался там, где оказался. */
export type Why =
  /** Обычный вход: первый, по закладке, после выхода. Ведёт на дашборд. */
  | "dashboard"
  /** Повторный вход после перерыва: поверхность выдана, продолжаем работу. */
  | "return"
  /** Повторный вход после перерыва: поверхность за перерыв закрыли. */
  | "revoked";

export interface Where {
  /** Код поверхности, на которую ведёт вход. */
  readonly code: string;
  readonly why: Why;
}

/**
 * Куда ведёт состоявшийся вход.
 *
 * `goesTo` — код с сервера (`LoggedIn.goes_to`, он же `S-52` по `G93`); клиент
 * его не выбирает и не подставляет свой.
 * `granted` — `/me`.surfaces: коды, которые человеку сейчас видны. Поверхности
 * с отметкой «не видно даже в меню» сервер в этот перечень не кладёт, поэтому
 * «выдана» здесь — это «есть ключ», а не разбор значения.
 *
 * ⚠ Ключевое: **прерванный адрес сам по себе права не даёт.** Пока проверки
 * `granted` не было, человек, у которого поверхность забрали за время перерыва,
 * возвращался бы на неё по памяти браузера.
 */
export function afterLogin(args: {
  readonly goesTo: string;
  readonly interrupted: Interrupted | null;
  readonly granted: Readonly<Record<string, string>>;
}): Where {
  const { goesTo, interrupted: where, granted } = args;
  if (where === null) return { code: goesTo, why: "dashboard" };
  if (Object.prototype.hasOwnProperty.call(granted, where.code)) {
    return { code: where.code, why: "return" };
  }
  return { code: goesTo, why: "revoked" };
}
